import { MIN_DATE } from '@/components/form/dateConstants';
import { DateInput } from '@/components/form/DateInput';
import { DatePickerField } from '@/components/form/DatePickerField';
import { CalendarIcon } from '@/components/icons/CalendarIcon';
import { FormControl, FormField, FormItem } from '@/components/primitives/Form';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { calendarDateToIso, format, isoToCalendarDate } from '@/utils/dateTime';
import { getBcp47Locale } from '@/utils/locale';
import { getLocalTimeZone, today } from '@internationalized/date';
import {
  Button,
  Calendar,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import { useMemo, useState, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

interface BirthDateFieldProps {
  name: string;
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

// Birth-date picker shared by the account "My Information" form and signup.
// Uses the inline DatePickerField on laptop+, and a full-screen calendar overlay
// on smaller screens where the inline popover is too cramped.
export const BirthDateField = ({
  name,
  label = <TransText i18nKey="birthDate" />,
  required,
  disabled,
}: BirthDateFieldProps) => {
  const form = useFormContext();
  const { t } = useTranslation();
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });
  const isTabletOrBigger = useMediaQuery({ minWidth: breakpoints.tablet });
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const value = form.watch(name);
  const calendarValue = useMemo(() => isoToCalendarDate(value), [value]);
  const todayAriaDate = today(getLocalTimeZone());

  if (disabled) {
    return (
      <FormField
        name={name}
        render={({ field }) => {
          const selectedDate = isoToCalendarDate(field.value)?.toDate(
            getLocalTimeZone()
          );

          return (
            <FormItem>
              <FormControl label={label} required={required}>
                <Button
                  isDisabled
                  data-testid="birth-date-disabled"
                  className="flex justify-start bg-white text-center text-neutral-medium hover:bg-white"
                >
                  <CalendarIcon />
                  {selectedDate ? (
                    format(selectedDate, 'dd/MM/yyyy')
                  ) : (
                    <TransText i18nKey="pickADate" />
                  )}
                </Button>
              </FormControl>
            </FormItem>
          );
        }}
      />
    );
  }

  if (isLaptopOrBigger) {
    return (
      <DatePickerField
        name={name}
        label={label}
        aria-label={t('text.birthDate')}
        required={required}
        classNames={{ trigger: 'bg-white' }}
      />
    );
  }

  return (
    <>
      <FormField
        name={name}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormControl label={label} required={required}>
              <DateInput
                value={
                  typeof field.value === 'string' ? field.value : undefined
                }
                onChange={field.onChange}
                onBlur={field.onBlur}
                maxValue={todayAriaDate}
                error={!!fieldState.error}
                aria-label={t('text.birthDate')}
                classNames={{ input: 'pr-10' }}
                data-testid="birth-date-input"
                endAdornment={
                  <Button
                    variant="icon"
                    size="small"
                    onClick={() => setIsOverlayOpen(true)}
                    aria-label={t('text.pickADate')}
                    data-testid="birth-date-trigger"
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                }
              />
            </FormControl>
          </FormItem>
        )}
      />

      {isOverlayOpen && (
        <div
          className={cn(
            'fixed inset-0 z-50',
            isTabletOrBigger && 'flex items-center justify-center'
          )}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 duration-300 animate-in fade-in"
            onClick={() => setIsOverlayOpen(false)}
          />
          {/* Panel */}
          <div
            className={cn(
              'flex flex-col justify-between gap-7 border-t bg-white p-6 text-dark shadow-lg duration-500 animate-in',
              isTabletOrBigger
                ? 'min-h-3/5 relative w-2/3 justify-between rounded-2xl py-10 fade-in'
                : 'absolute inset-x-0 bottom-0 rounded-t-2xl pb-10 slide-in-from-bottom'
            )}
          >
            <div className="flex flex-row justify-between">
              <Typography variant="heading2">{label}</Typography>
              <Button
                onClick={() => setIsOverlayOpen(false)}
                data-testid="close-calendar-overlay-button"
                variant="icon"
                className="bg-neutral-pale"
              >
                <Icons.x width={16} height={16} />
              </Button>
            </div>
            <Divider orientation="horizontal" grow={false} />
            <Calendar
              data-testid="calendar"
              value={calendarValue ?? null}
              defaultFocusedValue={calendarValue}
              onChange={(date) => {
                if (date) {
                  form.setValue(name, calendarDateToIso(date), {
                    shouldDirty: true,
                  });
                }
              }}
              pickers={true}
              locale={getBcp47Locale()}
              isDateUnavailable={(date) => date.compare(todayAriaDate) > 0}
              // Real bounds plus a spanning window give one stable year list
              // (1900..today) instead of a re-centering 50-year slice
              minValue={MIN_DATE}
              maxValue={todayAriaDate}
              visibleYears={todayAriaDate.year - MIN_DATE.year + 1}
              className="h-full max-w-xl self-center"
              autoFocus
            />
            <Button
              variant="primary"
              data-testid="confirm-birth-date-button"
              className="h-12"
              onClick={() => setIsOverlayOpen(false)}
            >
              <Typography
                variant="body1-bold"
                className="flex flex-row items-center gap-3"
              >
                <Icons.check width={18} height={18} />
                <TransText i18nKey="confirm" />
              </Typography>
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
